import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { BhiDialog, DialogCloseButton } from 'src/UIKit';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  FileContainer,
  ErrorMessage,
  Cont,
} from 'src/components/AttachFileUrlModal/styles';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TABLE_HEADER_ACTION_SX, TABLE_HEADER_ACTIONS_WRAP_SX } from 'src/components/TableHeader/TableHeader';
import { getDateTimeUSAFormat } from 'src/constants/helpers';
import { CompanyDocument, CompanyDocumentsTabViewModel } from 'src/modules/companies/types';
import SignLoaDialog from '../SignLoaDialog';
import DocumentPreviewModal from '../shared/components/DocumentPreviewModal';

const getFileIcon = (documentType: string) => {
  switch (documentType) {
    case 'w9':
      return <DescriptionIcon color="error" />;
    case 'loa':
      return <FileIcon color="primary" />;
    case 'phone_bill':
      return (
        <Box display="flex" color="success.dark">
          <FileIcon />
        </Box>
      );
    case 'custom':
      return (
        <Box display="flex" color="warning.light">
          <FileIcon />
        </Box>
      );
    default:
      return <FileIcon color="action" />;
  }
};

const CompanyDocumentsTabView = (props: CompanyDocumentsTabViewModel) => {
  const {
    company,
    documents,
    documentsCount,
    loading,
    uploading,
    uploadModalOpen,
    uploadFile,
    uploadDocumentType,
    documentTypeError,
    previewDocument,
    searchQuery,
    page,
    pageSize,
    sort,
    showDocumentTypeDialog,
    signLoaOpen,
    dropzoneStyle,
    getRootProps,
    getInputProps,
    ConfirmationDialog,
    getDocumentTypeLabel,
    reloadDocuments,
    changeSorting,
    onSearchChange,
    onPaginationChange,
    onOpenUploadModal,
    onChangeUploadDocumentType,
    onUpload,
    resetUploadState,
    onDelete,
    onPreview,
    onClosePreview,
    onOpenSignLoa,
    onCloseSignLoa,
    onCloseDocumentTypeDialog,
  } = props;

  return (
    <Stack mt={3.75} height="calc(100vh - 250px)">
      <ConfirmationDialog />

      <GridData
        header={
          <TableHeader
            searchValue={searchQuery}
            onSearchChange={onSearchChange}
            searchPlaceholder="Search documents..."
            actions={
              <Box sx={TABLE_HEADER_ACTIONS_WRAP_SX}>
                <Button
                  variant="contained"
                  startIcon={<DescriptionIcon />}
                  onClick={onOpenSignLoa}
                  sx={TABLE_HEADER_ACTION_SX}
                >
                  Sign LOA
                </Button>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={onOpenUploadModal}
                  sx={TABLE_HEADER_ACTION_SX}
                >
                  Upload Document
                </Button>
              </Box>
            }
          />
        }
        isLoading={loading}
        rows={documents}
        rowHeight={60}
        columns={[
          {
            field: 'fileName',
            headerName: 'Document Name',
            renderCell: (r) => (
              <Stack direction="row" alignItems="center" height="100%">
                {getFileIcon(r.row.documentType)}
                <Typography variant="body2" ml={1} noWrap>
                  {r.row.fileName}
                </Typography>
              </Stack>
            ),
            minWidth: 250,
            editable: false,
            sortable: true,
            flex: 2,
          },
          {
            field: 'documentType',
            headerName: 'Type',
            renderCell: (r) => (
              <Stack direction="row" alignItems="center" height="100%">
                <Typography variant="body2">
                  {getDocumentTypeLabel(r.row.documentType)}
                </Typography>
              </Stack>
            ),
            minWidth: 120,
            editable: false,
            sortable: true,
            flex: 1,
          },
          {
            field: 'dateCreated',
            headerName: 'Created Date',
            renderCell: (r) => (
              <Stack direction="row" alignItems="center" height="100%">
                {getDateTimeUSAFormat(new Date(r.row.dateCreated))}
              </Stack>
            ),
            minWidth: 150,
            editable: false,
            sortable: true,
            flex: 1,
          },
        ]}
        sorting={{
          value: sort,
          onChange: changeSorting,
        }}
        renderAction={(document: CompanyDocument) => (
          <>
            <IconButton
              size="small"
              onClick={() => onPreview(document)}
              color="primary"
              title="Watch Document"
            >
              <VisibilityIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onDelete(document)}
              color="error"
              title="Delete Document"
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </>
        )}
        pagination={{
          page,
          pageSize,
          totalCount: documentsCount,
          onChange: onPaginationChange,
        }}
      />

      {/* Upload Modal */}
      <BhiDialog
        open={uploadModalOpen}
        onClose={resetUploadState}
        maxWidth="sm"
        fullWidth
        title="Upload Document"
        showCloseButton={false}
      >
        <DialogCloseButton onClick={resetUploadState}>
          <CloseIcon />
        </DialogCloseButton>
        <DialogContent>
          <Box mb={2}>
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={uploadDocumentType}
                onChange={onChangeUploadDocumentType}
                disabled={uploading}
                label="Document Type"
              >
                <MenuItem value="w9">W9</MenuItem>
                <MenuItem value="loa">LOA (Letter of Authorization)</MenuItem>
                <MenuItem value="phone_bill">Phone Bill</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
            </FormControl>
            <Box minHeight={24} mt={1}>
              {documentTypeError && (
                <Typography variant="body2" color="error">
                  {documentTypeError}
                </Typography>
              )}
            </Box>
          </Box>

          <FileContainer style={{ marginBottom: '10px' }}>
            <div className="container">
              <Cont {...getRootProps({ style: dropzoneStyle })}>
                <input {...getInputProps()} />
                <p>Drag and drop {uploadDocumentType === 'custom' ? 'file' : 'PDF'} here, or click to select file</p>
                <div style={{ textAlign: 'center', padding: '0px 40px' }}>
                  {uploadDocumentType === 'custom' ? 'Any file type is allowed' : 'Only PDF files are allowed'}
                </div>
                {uploading ? (
                  <LoadingButton
                    loading
                    loadingPosition="start"
                    startIcon={<CloudUploadIcon />}
                    variant="outlined"
                    style={{ margin: '25px 0px 10px 0px' }}
                  >
                    Uploading
                  </LoadingButton>
                ) : (
                        <Button
                          component="label"
                          role={undefined}
                          variant="contained"
                          tabIndex={-1}
                          disabled={uploading}
                          startIcon={<CloudUploadIcon />}
                          style={{ margin: '25px 0px 10px 0px' }}
                        >
                          {uploadFile?.name ? uploadFile.name : `Select ${uploadDocumentType === 'custom' ? 'File' : 'PDF'}`}
                        </Button>
                )}
              </Cont>
            </div>
            <ErrorMessage></ErrorMessage>
          </FileContainer>
        </DialogContent>
        <DialogActions sx={{ mb: 2 }}>
          <Button variant="outlined" onClick={resetUploadState} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={onUpload}
            variant="contained"
            disabled={!uploadFile || !uploadDocumentType || !!documentTypeError || uploading}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </BhiDialog>

      {/* Sign LOA Dialog */}
      <SignLoaDialog
        open={signLoaOpen}
        onClose={onCloseSignLoa}
        onSuccess={reloadDocuments}
        company={company}
      />

      {/* Preview Modal */}
      <DocumentPreviewModal open={!!previewDocument} onClose={onClosePreview} document={previewDocument} />

      {/* Document Type Required Dialog */}
      <BhiDialog
        open={showDocumentTypeDialog}
        onClose={onCloseDocumentTypeDialog}
        maxWidth="sm"
        title="Document Type Required"
      >
        <DialogContent>
          <Typography>
            Please, select Document Type first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseDocumentTypeDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </BhiDialog>
    </Stack>
  );
};

export default CompanyDocumentsTabView;
